import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { InboxOutlined as InboxIcon } from '@mui/icons-material';
import { EmptyStateProps } from './types';
import { useEmptyState } from './useEmptyState';
import './EmptyState.scss';

const EmptyState: React.FC<EmptyStateProps> = props => {
  const {
    icon,
    title,
    description,
    content,
    actions = [],
    customStyles,
    maxWidth = 600,
  } = props;

  const { handleActionClick } = useEmptyState();

  const defaultIcon = <InboxIcon className="emptyState__defaultIcon" />;

  return (
    <Box
      data-testid="empty-state"
      className="emptyState"
      style={customStyles?.container}
      sx={{
        maxWidth,
        mx: 'auto',
      }}
    >
      {(icon || !content) && (
        <Box
          className="emptyState__iconContainer"
          style={customStyles?.iconContainer}
        >
          {icon || defaultIcon}
        </Box>
      )}

      <Box
        className={`emptyState__textContainer ${content ? 'emptyState__textContainer--withContent' : ''}`}
        style={customStyles?.textContainer}
      >
        <Typography
          data-testid="empty-state-title"
          variant="h5"
          component="h2"
          className={`emptyState__title ${description ? 'emptyState__title--withDescription' : ''}`}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            data-testid="empty-state-description"
            variant="body1"
            className="emptyState__description"
          >
            {description}
          </Typography>
        )}
      </Box>

      {content && (
        <Box
          className={`emptyState__contentContainer ${actions.length > 0 ? 'emptyState__contentContainer--withActions' : ''}`}
          style={customStyles?.contentContainer}
        >
          {content}
        </Box>
      )}

      {actions.length > 0 && (
        <Box
          className="emptyState__actionsContainer"
          style={customStyles?.actionsContainer}
        >
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
          >
            {actions.map((action, index) => (
              <Button
                key={index}
                data-testid={`empty-state-action-${index}`}
                variant={action.variant || 'contained'}
                color={action.color || 'primary'}
                onClick={() => handleActionClick(action.onClick)}
                disabled={action.disabled}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default EmptyState;
